// Shared helpers for variation naming.
// `name` is user-facing (flexible). `tag` is the strict email sub-address.

export const VARIATION_NAME_MAX = 25;
const ALLOWED_NAME_RE = /^[A-Za-z0-9 _.\-()]+$/;

/** Trim and collapse internal whitespace. Does not validate. */
export function normalizeVariationName(input: string): string {
  return input.trim().replace(/\s+/g, " ");
}

/**
 * Validate a user-supplied variation name.
 * Returns an error message or null if valid.
 */
export function validateVariationName(input: string): string | null {
  const name = normalizeVariationName(input);
  if (name.length === 0) return "Name cannot be empty";
  if (name.length > VARIATION_NAME_MAX) {
    return `Name must be ${VARIATION_NAME_MAX} characters or fewer`;
  }
  if (!ALLOWED_NAME_RE.test(name)) {
    return "Only letters, numbers, spaces, and - _ . ( ) are allowed";
  }
  return null;
}

/**
 * Derive a strict, email-safe tag from a display name.
 * Returns "" if nothing usable remains; callers should fall back.
 */
export function deriveTagFromName(input: string): string {
  return normalizeVariationName(input)
    .toLowerCase()
    .replace(/[\s.()]/g, "-")
    .replace(/[^a-z0-9_-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^[-_]+|[-_]+$/g, "")
    .substring(0, 50);
}

/**
 * Given a desired tag and the set of tags already in use, return a
 * collision-free tag by appending -2, -3, ... when needed.
 * Empty input falls back to "variation".
 */
export function uniqueTag(desired: string, taken: Iterable<string>): string {
  const set = new Set(taken);
  const base = desired || "variation";
  if (!set.has(base)) return base;
  let i = 2;
  while (set.has(`${base}-${i}`)) i++;
  return `${base}-${i}`.substring(0, 50);
}
